// Path: src/lib/apis_publish.ts
// Author: ISTP_Dev
// Description: API functions for students and teachers to browse published courses, lesson plans, and courseware based on their permissions

import { api } from './api';

// ==========================
// 📚 Type Definitions
// ==========================

// Slide summary for list views
export interface PublicSlideSummaryOut {
  id: number;
  slide_index: number;
  version_number: string;
  is_final: boolean;
  created_by: number;
  updated_at: string;
}

// Lesson details with slides (full mode)
export interface PublicLessonOut {
  id: number;
  title: string;
  cover_image_url?: string;
  learning_objectives: string;
  prerequisites: string;
  sort_order: number;
  slides: PublicSlideSummaryOut[];
  slide_count?: number;
}

// Lesson summary (summary mode, no slides array)
export interface PublicLessonSummaryOut {
  id: number;
  title: string;
  cover_image_url?: string;
  learning_objectives: string;
  sort_order: number;
  slide_count: number;
}

// Course stage with lessons (full mode)
export interface PublicCourseStageOut {
  id: number;
  title: string;
  sort_order: number;
  lessons: PublicLessonOut[];
}

// Course stage (summary mode)
export interface PublicCourseStageSummaryOut {
  id: number;
  title: string;
  sort_order: number;
  lessons: PublicLessonSummaryOut[];
}

// Full course outline (full mode)
export interface PublicCourseOut {
  id: number;
  title: string;
  description: string;
  stages: PublicCourseStageOut[];
  cover_image_url?: string | null;
}

// Course outline (summary mode)
export interface PublicCourseSummaryOut {
  id: number;
  title: string;
  description: string;
  stages: PublicCourseStageSummaryOut[];
  cover_image_url?: string | null;
}

// Full slide content
export interface FullSlideContentOut {
  id: number;
  html_code: string;
  slide_index: number;
  side_guide?: string; // 每页教案内容
}

// Prev/Next slide navigation
export interface SlidePrevNextOut {
  prev: number | null;
  next: number | null;
}

// ==========================
// 📖 Student Course API Functions
// ==========================

/**
 * Get all courses the current user has view_only permission for
 * Returns: PublicCourseSummaryOut[] - Course outlines with stages, lessons (without full slides array)
 */
export const getStudentCourses = () => 
  api.get<PublicCourseSummaryOut[]>('/student-courses/courses?mode=summary');

/**
 * Get all courses in full mode (includes slides array)
 */
export const getStudentCoursesFull = () =>
  api.get<PublicCourseOut[]>('/student-courses/courses?mode=full');

/**
 * Get detailed information for a specific published lesson
 * @param lessonId The ID of the lesson to fetch
 * Returns: PublicLessonOut - Lesson details with slide summaries
 */
export const getStudentLessonDetail = (lessonId: number) => 
  api.get<PublicLessonOut>(`/student-courses/lessons/${lessonId}`);

/**
 * Get complete content for a published slide
 * @param slideId The ID of the slide to fetch
 * Returns: FullSlideContentOut - Complete slide content with HTML and side guide
 */
export const getStudentSlideContent = (slideId: number) => 
  api.get<FullSlideContentOut>(`/student-courses/slides/${slideId}/content`);

/**
 * Get previous and next slide IDs for navigation
 * Automatically handles permission-based table selection (public vs internal)
 * @param slideId The ID of the current slide
 * Returns: SlidePrevNextOut - Previous and next slide IDs
 */
export const getStudentSlidePrevNext = (slideId: number) => 
  api.get<SlidePrevNextOut>(`/student-courses/slides/${slideId}/prev-next`);

// ==========================
// 📖 Teacher Course API Functions (Alias for consistency)
// ==========================
// Note: Teachers use the same endpoints as students for viewing published content
// These aliases provide clearer naming for teacher-facing components

/**
 * Get all courses the current teacher has access to (alias for getStudentCourses)
 */
export const getTeacherCourses = getStudentCourses;

/**
 * Get detailed information for a specific published lesson (alias for getStudentLessonDetail)
 */
export const getTeacherLessonDetail = getStudentLessonDetail;

/**
 * Get complete content for a published slide (alias for getStudentSlideContent)
 */
export const getTeacherSlideContent = getStudentSlideContent;

/**
 * Get previous and next slide IDs for navigation (alias for getStudentSlidePrevNext)
 */
export const getTeacherSlidePrevNext = getStudentSlidePrevNext;
